import { useQuery } from "@tanstack/react-query";
import type { SessionMethodReturn, SessionStandardQueryMethodName } from "../types";
import type { UseTellescopeSessionQueryOptions, UseTellescopeSessionQueryResult } from "./types";
import { buildSessionQueryKey } from "../keys";
import { useTellescopeSession } from "../context";
import { executeTellescopeSessionMethod } from "../utils/execute-tellescope-session-method";
import { useMemo } from "react";
import { addKeyToQueryResult } from "../utils";

/**
 * React hook for querying data from Tellescope session methods using TanStack Query.
 * Only supports standard (non-paginated) query methods.
 *
 * This hook wraps TanStack Query's `useQuery` hook with Tellescope session-specific functionality,
 * providing type-safe access to session methods with automatic query key generation and caching.
 *
 * @template Method - The session query method name (e.g., 'sync', 'test_authenticated')
 * @template TError - The error type (defaults to Error)
 * @template TData - The returned data type (defaults to the method's return type)
 *
 * @param options - Query configuration options
 * @param options.method - The session method to call (e.g., 'sync', 'test_authenticated')
 * @param options.request - The request parameters for the session method
 * @param options.session - Optional Tellescope session to use instead of the context session
 * @param options...tanstackOptions - All TanStack Query options are supported except `queryKey` and `queryFn` (which are automatically generated)
 * @returns TanStack Query result object with data, loading state, error state, and refetch utilities, as well as the computed query key
 *
 * @example
 * ```tsx
 * // Sync data from a specific date
 * const { data, isLoading, error } = useTellescopeSessionQuery({
 *   method: 'sync',
 *   request: { from: new Date('2024-01-01') }
 * });
 *
 * // Test authentication status
 * const { data: isAuthenticated } = useTellescopeSessionQuery({
 *   method: 'test_authenticated',
 *   request: undefined,
 *   retry: false,
 *   staleTime: Infinity,
 * });
 *
 * // Query with TanStack Query options
 * const { data } = useTellescopeSessionQuery({
 *   method: 'bulk_load',
 *   request: {
 *     load: [
 *       { model: 'users', options: { limit: 100 } },
 *       { model: 'appointments', options: { from: new Date() } },
 *     ],
 *   },
 *   enabled: isReady,
 *   staleTime: 5000,
 * });
 * ```
 */
export const useTellescopeSessionQuery = <
  Method extends SessionStandardQueryMethodName,
  TError = Error,
  TData = SessionMethodReturn<Method>,
>({
  method,
  request,
  session: providedSession,
  requestKeysToOmitFromQueryKey,
  requestSideEffect,
  responseSideEffect,
  ...tanstackOptions
}: UseTellescopeSessionQueryOptions<Method, TError, TData>): UseTellescopeSessionQueryResult<Method, TError, TData> => {
  const session = useTellescopeSession(providedSession);

  const queryKey = useMemo(
    () => buildSessionQueryKey<Method>({ method, request, requestKeysToOmitFromQueryKey }),
    [method, request, requestKeysToOmitFromQueryKey]
  );

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should be disabled if session is `null`");
      }

      requestSideEffect?.(request);
      const res = await executeTellescopeSessionMethod({
        session,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      });
      responseSideEffect?.(request, res);
      return res;
    },
    ...tanstackOptions,
    enabled: !!session && (tanstackOptions?.enabled ?? true),
  });

  return addKeyToQueryResult(queryResult, queryKey);
};
